import {
  CREDIT_BUILDER_ENROLL_FAIL,
  CREDIT_BUILDER_ENROLL_START,
  CREDIT_BUILDER_ENROLL_SUCCESS,
  STORE_CREDIT_BUILDER_SECURITIZATION,
} from 'types/cashBuilder.types';
import {
  postCreateSecuredBoosterCard,
  postOptInToBooster,
} from 'externals/_services/banking.service';
import { getAllCardDetails, getCashProfile } from 'actions/sesameCash.actions';
import { createLogger } from 'helpers/logger';
import {
  deleteBuilderAccount,
  fetchBankingConfig,
  fetchBuilderRecentTransaction,
  fetchBuilderStatements,
  fetchSecuredCardSettings,
  patchSecuredCardSettings,
  patchUIState,
  postPurseWithdraw,
  postSecuredCardPayment,
  securedCardV2OptIn,
} from 'externals/_services/builder.service';
import {
  BANKING_CONFIG_PAYLOAD,
  buildPayloadDispatcher,
  RECENT_BUILDER_TRANSACTIONS_PAYLOAD,
  SECURED_CARD_SETTINGS_PAYLOAD,
  SECURED_CARD_STATEMENTS_PAYLOAD,
} from 'types/payload.constants';
import { SET_UTILIZATION_VALUE } from 'types/sesameCash.constants';
import { buildPayloadTemplate } from 'actions/payload.action';

const logger = createLogger({
  name: 'cashBuilder.actions',
});

export const storeCreditBuilderSecuritization = creditBuilderAmount => ({
  type: STORE_CREDIT_BUILDER_SECURITIZATION,
  creditBuilderAmount,
});

export const enrollUserInBooster =
  securityDepositAmount => (dispatch, getState) => {
    return new Promise((resolve, reject) => {
      const state = getState();
      const cashToken = state?.sesameCash?.cashPinToken;

      dispatch({ type: CREDIT_BUILDER_ENROLL_START });
      postOptInToBooster(cashToken)
        .then(() => {
          return postCreateSecuredBoosterCard(cashToken, {
            securityDepositAmount,
          });
        })
        .then(() => {
          //refresh all mutated dependencies
          return Promise.all([
            dispatch(getAllCardDetails(cashToken)),
            dispatch(getCashProfile()),
            dispatch(getBankingConfig()),
            dispatch(getBuilderCardSettings()),
            dispatch(getBuilderStatementMetadata(cashToken)),
            dispatch(getAllCardDetails(cashToken)),
          ]);
        })
        .then(() => {
          dispatch({ type: CREDIT_BUILDER_ENROLL_SUCCESS });
          resolve();
        })
        .catch(err => {
          logger.reportAPIError(err);
          dispatch({ type: CREDIT_BUILDER_ENROLL_FAIL });
          reject(err);
        });
    });
  };

export const deleteBuilderDebitAccount = () => async (dispatch, getState) => {
  const state = getState();
  const token = state?.sesameCash?.cashPinToken;

  try {
    await deleteBuilderAccount(token);
    await Promise.all([
      dispatch(getCashProfile()),
      dispatch(getAllCardDetails(token)),
    ]);

    return;
  } catch (err) {
    logger.reportAPIError(err);
    throw err;
  }
};

const recentStatementPayloadAction = (
  recentSecuredCardStatements,
  recentSecuredCardStatementsLoading,
  recentSecuredCardStatementsError,
) => ({
  type: SECURED_CARD_STATEMENTS_PAYLOAD,
  recentSecuredCardStatementsLoading,
  recentSecuredCardStatements,
  recentSecuredCardStatementsError,
});

export const getBuilderStatementMetadata = () => async (dispatch, getState) => {
  const state = getState();
  const token = state?.sesameCash?.cashPinToken;

  try {
    dispatch(recentStatementPayloadAction(null, true, null));
    const recentSecuredCardStatements = await fetchBuilderStatements(token, 3);
    dispatch(
      recentStatementPayloadAction(recentSecuredCardStatements, false, null),
    );

    return recentSecuredCardStatements;
  } catch (err) {
    dispatch(recentStatementPayloadAction(null, false, err));

    logger.reportAPIError(err);
    throw err;
  }
};

const securedCardSettingsPayloadAction = (
  securedCardSettings,
  securedCardSettingsLoading,
  securedCardSettingsError,
) => ({
  type: SECURED_CARD_SETTINGS_PAYLOAD,
  securedCardSettingsLoading,
  securedCardSettings,
  securedCardSettingsError,
});

export const getBuilderCardSettings = () => async (dispatch, getState) => {
  const state = getState();
  const token = state?.sesameCash?.cashPinToken;

  try {
    dispatch(securedCardSettingsPayloadAction(null, true, null));
    const securedCardSettings = await fetchSecuredCardSettings(token);
    dispatch(
      securedCardSettingsPayloadAction(securedCardSettings, false, null),
    );

    return securedCardSettings;
  } catch (err) {
    dispatch(securedCardSettingsPayloadAction(null, false, err));

    logger.reportAPIError(err);
    throw err;
  }
};

const recentBuilderTransactionPayloadAction = buildPayloadDispatcher(
  'recentBuilderTransaction',
  RECENT_BUILDER_TRANSACTIONS_PAYLOAD,
);
const getBuilderRecentTransactionsActionTemplate = buildPayloadTemplate(
  recentBuilderTransactionPayloadAction,
  fetchBuilderRecentTransaction,
);
export const getBuilderRecentTransactions =
  () => async (dispatch, getState) => {
    const state = getState();
    const token = state?.sesameCash?.cashPinToken;

    try {
      const recentBuilderTransaction = await dispatch(
        getBuilderRecentTransactionsActionTemplate(token, 25),
      );

      return recentBuilderTransaction;
    } catch (err) {
      logger.reportAPIError(err);
      throw err;
    }
  };

export const withdrawVault =
  vaultWithdrawalAmount => async (dispatch, getState) => {
    const state = getState();
    const token = state?.sesameCash?.cashPinToken;

    try {
      await postPurseWithdraw(token, vaultWithdrawalAmount);
      await Promise.all([
        dispatch(getBankingConfig()),
        dispatch(getBuilderRecentTransactions()),
        dispatch(getBuilderCardSettings()),
        dispatch(getBuilderStatementMetadata(token)),
        dispatch(getAllCardDetails(token)),
      ]);
      return true;
    } catch (err) {
      logger.reportAPIError(err);
      throw err;
    }
  };

export const setBuilderUtilization = maxUtilizationAmount => ({
  type: SET_UTILIZATION_VALUE,
  maxUtilizationAmount,
});

const setUiStateSecuredCardUtilization = async token => {
  await patchUIState(token, { name: 'securedCardUtilization', state: true });
};

export const updatePaymentType =
  utilizationMethod => async (dispatch, getState) => {
    const state = getState();
    const token = state?.sesameCash?.cashPinToken;
    let apiBody = {
      utilizationMethod,
    };
    try {
      await patchSecuredCardSettings(token, apiBody);
    } catch (err) {
      logger.reportAPIError(err);
      throw err;
    }
  };

export const optinV2 = token => async () => {
  try {
    await securedCardV2OptIn(token);
  } catch (err) {
    logger.reportAPIError(err);
    throw err;
  }
};

export const finalizeUtilizationAndPaymentType =
  (utilizationMethod, utilizationAmount = true) =>
  async (dispatch, getState) => {
    const state = getState();
    const token = state?.sesameCash?.cashPinToken;
    const debitCard = state?.allCardDetails?.cards?.find(
      c => c?.type === 'CREDIT',
    );
    let apiBody = {
      utilizationMethod,
    };
    if (utilizationAmount) {
      apiBody.maxUtilizationAmount = state?.sesameCash?.maxUtilizationAmount;
    }

    try {
      await patchSecuredCardSettings(token, apiBody);
      await setUiStateSecuredCardUtilization(token, true);
      await Promise.all([
        dispatch(getBankingConfig()),
        dispatch(getBuilderCardSettings()),
        dispatch(getBuilderStatementMetadata(token)),
        dispatch(getAllCardDetails(token)),
        debitCard ? dispatch(getBuilderRecentTransactions()) : null,
      ]);
    } catch (err) {
      logger.reportAPIError(err);
      throw err;
    }
  };

const bankingConfigPayloadAction = buildPayloadDispatcher(
  'bankingConfig',
  BANKING_CONFIG_PAYLOAD,
);
const getBankingConfigInnerAction = buildPayloadTemplate(
  bankingConfigPayloadAction,
  fetchBankingConfig,
);
export const getBankingConfig = () => async (dispatch, getState) => {
  const state = getState();
  const token = state?.sesameCash?.cashPinToken;

  try {
    const c = await dispatch(getBankingConfigInnerAction(token));

    return c;
  } catch (err) {
    logger.reportAPIError(err);
    throw err;
  }
};

export const payBuilderBalanceFromAccount =
  (source, paymentAmount) => async (dispatch, getState) => {
    const state = getState();
    const token = state?.sesameCash?.cashPinToken;

    try {
      const paymentResponse = await postSecuredCardPayment(token, {
        paymentAmount,
        source,
      });

      await Promise.all([
        dispatch(getCashProfile()),
        dispatch(getBankingConfig()),
        dispatch(getBuilderRecentTransactions()),
        dispatch(getBuilderCardSettings()),
        dispatch(getBuilderStatementMetadata(token)),
        dispatch(getAllCardDetails(token)),
      ]);

      return paymentResponse;
    } catch (err) {
      logger.reportAPIError(err);
      throw err;
    }
  };
